import React, { useContext, useEffect, useMemo, useState } from 'react';
import '@d3fc/d3fc-element';
import { extentLinear } from '@d3fc/d3fc-extent';
import AppContext from 'app/AppContext';
import { usePortfolioSelectedPosition } from 'portfolio/store/hooks';
import TimeSeriesChart from 'utils/components/TimeSeriesChart/TimeSeriesChart';
import { useYScale } from 'utils/components/TimeSeriesChart/yAxis';
import AreaGradient from 'utils/components/TimeSeriesChart/AreaGradient';
import 'utils/components/RevisionNumber/RevisionNumber.scss';
import { combineToggleableClassnames } from 'utils/toggleableClassnames/toggleableClassnames';
import {
    FillsChartType,
    FillsGraphConfiguration,
    OrderFillsConfig,
} from '../store/configuration/FillsGraphConfiguration';
import {
    deriveAverageExecutionPrice,
    deriveCandleHigh,
    deriveCandleLow,
    deriveLimitPrice,
    deriveNormalisedBenchmarkPrice,
    deriveOrderFillBucketFillPrice,
    derivePrice,
    deriveVwap,
    FillsGraphData,
    OrderFill,
} from '../fillsGraphData';
import styleConstants from '../variables.module.scss';
import YAxisLabel from '../axisLabel/YAxisLabel';
import OrderFillsChartLegend from './OrderFillsChartLegend';
import { orderFillsSeries } from './series/orderFillsSeries';
import { deriveOrderFillsYAxisLabelProps } from './orderFillsChartLabels';
import './OrderFillsChart.scss';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { tickSizeFormatFn } from 'utils/tickSize';
import { OrderFillsChartXAxisLabels } from 'widgets/fillsGraph/orderFills/OrderFillsChartXAxisLabels';

const xAxisHeight = parseInt(styleConstants.orderFillsXAxisHeight);

/* eslint-disable react-hooks/exhaustive-deps */

export interface OrderFillsChartProps {
    chartHeight: number;
    fillsGraphContainerLeft: number;
    fillsGraphData: FillsGraphData;
    fillsGraphPortfolioData: FillsGraphPortfolioData;
    config: FillsGraphConfiguration;
    displayXAxis: boolean;
    width: number;
}

export default function OrderFillsChart(props: OrderFillsChartProps) {
    const {
        width,
        chartHeight,
        fillsGraphContainerLeft,
        fillsGraphData,
        fillsGraphPortfolioData,
        config,
        displayXAxis,
    } = props;

    const {
        sideClass,
        data,
        snapPrice,
        closePrice,
        open,
        strategyParameterData,
        strategyParametersLimitPriceAvailable,
        tickSize,
        arrivalTimestamp,
        endTimestamp,
    } = fillsGraphData;

    const { totalOrderQty, hasFullOrderData, orderLimitPrice } =
        fillsGraphPortfolioData;

    const {
        [FillsChartType.OrderFills]: {
            displayOrderFillSize,
            displayPrice,
            displayPriceCandles,
            displayVWAP,
            displayAvgExecution: displayAvgExecutionConfig,
            displayPercentageCompleted: displayPercentageCompletedConfig,
            displayArrival: displayArrivalConfig,
            displayClose,
            displayLimitPrice,
            hideOpen,
            displayAlgorithmParametersRevisions,
            displayIndex,
            avgExecAndVwapDecimals,
        },
    } = config;

    const displayAvgExecutionSeries =
        displayAvgExecutionConfig && hasFullOrderData;
    const displayPercentageCompletedSeries =
        displayPercentageCompletedConfig && hasFullOrderData;
    const displayArrivalSeries = displayArrivalConfig && hasFullOrderData;

    const limitPriceAccessor: (d: OrderFill) => number | undefined = useMemo(
        () =>
            strategyParametersLimitPriceAvailable
                ? deriveLimitPrice
                : () => orderLimitPrice,
        [strategyParametersLimitPriceAvailable, orderLimitPrice]
    );

    const yDomain = getYDomain(
        data,
        snapPrice,
        closePrice,
        open,
        config[FillsChartType.OrderFills],
        limitPriceAccessor
    );
    const yTicks = 6;

    if (Array.isArray(yDomain) && yDomain.length === 2) {
        if (yDomain[0] === yDomain[1]) {
            yDomain[0] = yDomain[0] * 0.8;
            yDomain[1] = yDomain[1] * 1.2;
        }
    }
    const yScale = useYScale(yDomain, [0, 1]);

    const position = usePortfolioSelectedPosition();

    const [series, setSeries] = useState();
    useEffect(() => {
        setSeries(() =>
            orderFillsSeries(
                yDomain[0],
                yDomain[1],
                yTicks,
                snapPrice,
                closePrice,
                open,
                totalOrderQty,
                position,
                limitPriceAccessor
            )
        );
    }, [
        data,
        yDomain[0],
        yDomain[1],
        yTicks,
        snapPrice,
        closePrice,
        totalOrderQty,
        position,
        limitPriceAccessor,
    ]);

    const plotAreaHeight = displayXAxis
        ? chartHeight - xAxisHeight
        : chartHeight;

    const cssClasses = combineToggleableClassnames(
        {
            classname: 'OrderFillsChart',
        },
        { classname: sideClass },
        { classname: 'hide-x-axis', hideClassname: displayXAxis },
        {
            classname: 'hide-order-fill-size',
            hideClassname: displayOrderFillSize,
        },
        {
            classname: 'hide-price',
            hideClassname: displayPrice,
        },
        {
            classname: 'hide-vwap',
            hideClassname: displayVWAP,
        },
        {
            classname: 'hide-avg-exec',
            hideClassname: displayAvgExecutionSeries,
        },
        {
            classname: 'hide-arrival',
            hideClassname: displayArrivalSeries,
        },
        {
            classname: 'hide-close',
            hideClassname: displayClose,
        },
        {
            classname: 'hide-percentage-complete',
            hideClassname: displayPercentageCompletedSeries,
        },
        {
            classname: 'hide-limit-price',
            hideClassname: displayLimitPrice,
        },
        {
            classname: 'hide-open',
            hideClassname: !hideOpen,
        },
        {
            classname: 'hide-revisions',
            hideClassname: displayAlgorithmParametersRevisions,
        },
        {
            classname: 'hide-index',
            hideClassname: displayIndex,
        },
        { classname: 'hide-candles', hideClassname: displayPriceCandles }
    );

    const { numberFormat } = useContext(AppContext);

    const lastLimitPrice = strategyParametersLimitPriceAvailable
        ? strategyParameterData?.[strategyParameterData.length - 1]?.limitPrice
        : orderLimitPrice;

    const yAxisLabelProps = deriveOrderFillsYAxisLabelProps(
        displayArrivalSeries ? snapPrice : undefined,
        displayClose ? closePrice : undefined,
        displayLimitPrice ? lastLimitPrice : undefined,
        !hideOpen ? open : undefined,
        numberFormat,
        yScale,
        plotAreaHeight,
        width
    );

    return (
        <div className={cssClasses}>
            <OrderFillsChartLegend
                avgExecAndVwapDecimals={avgExecAndVwapDecimals}
                fillsGraphData={fillsGraphData}
                fillsGraphPortfolioData={fillsGraphPortfolioData}
                fillsGraphContainerLeft={fillsGraphContainerLeft}
            />
            <div className="chart-container">
                {yAxisLabelProps.arrival && (
                    <YAxisLabel {...yAxisLabelProps.arrival} />
                )}
                {yAxisLabelProps.close && (
                    <YAxisLabel {...yAxisLabelProps.close} />
                )}
                {yAxisLabelProps.limit && (
                    <YAxisLabel {...yAxisLabelProps.limit} />
                )}
                {yAxisLabelProps.open && (
                    <YAxisLabel {...yAxisLabelProps.open} />
                )}
                <OrderFillsChartXAxisLabels
                    arrivalTimestamp={arrivalTimestamp}
                    endTimestamp={endTimestamp}
                    strategyParameterData={strategyParameterData ?? []}
                    plotAreaHeight={plotAreaHeight}
                />
                <TimeSeriesChart
                    id="OrderFillsChart"
                    height={chartHeight}
                    data={data}
                    series={series}
                    displayXAxis={displayXAxis}
                    yScale={yScale}
                    yTicks={yTicks}
                    yTickFormat={tickSizeFormatFn(numberFormat, tickSize)}
                />
                <AreaGradient
                    identifier={'fillPercentAreaGradient'}
                    startOffset="0%"
                    stopOffset="2.5%"
                />
            </div>
        </div>
    );
}

export function getYDomain(
    data: OrderFill[],
    snapPrice: number | undefined,
    closePrice: number | undefined,
    open: number | undefined,
    config: OrderFillsConfig,
    limitPriceAccessor: (d: OrderFill) => number | undefined
) {
    const undefinedAccessor = () => undefined;
    return (
        data.length &&
        extentLinear()
            .accessors([
                config.displayPrice ? derivePrice : undefinedAccessor,
                config.displayVWAP ? deriveVwap : undefinedAccessor,
                config.displayAvgExecution
                    ? deriveAverageExecutionPrice
                    : undefinedAccessor,
                (d: OrderFill) => {
                    const p = deriveOrderFillBucketFillPrice(d);
                    return p === 0 ? undefined : p;
                },
                () => (config.displayArrival ? snapPrice : undefined),
                () => (config.displayClose ? closePrice : undefined),
                () => (!config.hideOpen ? open : undefined),
                config.displayLimitPrice
                    ? limitPriceAccessor
                    : undefinedAccessor,
                config.displayIndex
                    ? deriveNormalisedBenchmarkPrice
                    : undefinedAccessor,
                config.displayPriceCandles
                    ? deriveCandleHigh
                    : undefinedAccessor,
                config.displayPriceCandles
                    ? deriveCandleLow
                    : undefinedAccessor,
            ])
            .pad([0.1, 0.1])(data)
    );
}
